import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { api } from '../config/api';
import ContainerNav from './ContainerNav';
import POReceiptingReport from './POReceiptingReport';

interface POReportItem {
  po_number: string;
  supplier_code: string;
  container_count: number;
  unique_stock_codes: number;
  total_lines: number;
  total_sheets: number;
  total_kg: number;
  first_delivery: string | null;
  last_delivery: string | null;
  due_diligence_completed: boolean;
  has_order_data: boolean;
  total_ordered_qty: number;
}

const POReports: React.FC = () => {
  const [poList, setPOList] = useState<POReportItem[]>([]);
  const [filteredPOs, setFilteredPOs] = useState<POReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<string>('');

  useEffect(() => {
    loadPOList();
  }, []);

  useEffect(() => {
    filterPOs();
  }, [searchTerm, poList]);

  const loadPOList = async () => {
    try {
      setLoading(true);
      const response = await api.get('/po-reports-list');
      if (response.data.success) {
        setPOList(response.data.po_list || []);
        setFilteredPOs(response.data.po_list || []);
      } else {
        setError('Failed to load PO data');
      }
    } catch (err) {
      console.error('Error loading POs:', err);
      setError('Error loading PO data');
    } finally {
      setLoading(false);
    }
  };

  const filterPOs = () => {
    let filtered = poList;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(po =>
        po.po_number.toLowerCase().includes(term) ||
        po.supplier_code?.toLowerCase().includes(term)
      );
    }

    setFilteredPOs(filtered);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };


  const handleRowClick = (poNumber: string) => {
    setSelectedPO(poNumber);
    setReportOpen(true);
  };

  if (loading) {
    return (
      <Box p={3}>
        <ContainerNav title="PO Reports" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <ContainerNav title="PO Reports" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Search PO number or supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />

          <Box flex={1} />

          <Typography variant="body2" color="textSecondary">
            {filteredPOs.length} PO{filteredPOs.length !== 1 ? 's' : ''} with receipt data
          </Typography>
        </Box>
      </Paper>

      {/* PO List Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>PO Number</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">SKUs</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Containers</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Last Container Delivered</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Due Diligence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPOs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" py={4}>
                    No POs with receipt data found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPOs.map((po) => (
                <TableRow
                  key={po.po_number}
                  hover
                  onClick={() => handleRowClick(po.po_number)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography fontWeight="bold">{po.po_number}</Typography>
                  </TableCell>
                  <TableCell>{po.supplier_code}</TableCell>
                  <TableCell align="center">{po.unique_stock_codes}</TableCell>
                  <TableCell align="center">{po.container_count}</TableCell>
                  <TableCell>{formatDate(po.last_delivery)}</TableCell>
                  <TableCell align="center">
                    {po.due_diligence_completed ? (
                      <CheckIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PO Receipting Report Dialog */}
      <POReceiptingReport
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        initialPONumber={selectedPO}
      />
    </Box>
  );
};

export default POReports;
